/**
 * Copilootvlak (lichtpaars), ontwerp 26, JP-keuze 3:
 * - bovenin alleen afwijkingen: een rood blok "Niet gevonden" of
 *   "Twijfel" als de motor iets niet kon controleren;
 * - altijd de regel "Wat is gecontroleerd (N)" die uitklapt naar de
 *   volledige lijst met bron per regel. Beheer-instelling "Copiloot toont
 *   alleen afwijkingen": aan (of niet gezet, laden, fout) = dicht bij
 *   start, uit = altijd de volledige lijst. Iedere medewerker leest hem
 *   (sessie 72, `GET /instellingen/scherm`);
 * - daaronder het gesprek met de copiloot en onderin één veld.
 *
 * Sessie 72 (punt 11): blok en regel staan vast boven het gesprek, niet in
 * het scrollvlak. Dat vlak springt naar het laatste bericht; na een paar
 * beurten stonden de afwijkingen daardoor buiten beeld.
 *
 * Het vlak hangt per zaak (key in Rechts): bij een andere zaak begint
 * alles leeg en breekt een lopende beurt af. De draad zelf komt per zaak
 * van de server. Een aangepast concept gaat via `zetConcept` direct naar
 * het antwoordvak in het midden; de copiloot verstuurt nooit zelf.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { LesKaart, LesTeller } from "../../Werkplek/Copiloot/LesKaart";
import { isLeerDit, lesTeLang } from "../../Werkplek/Copiloot/leerDit";
import { useLesDraad } from "../../Werkplek/Copiloot/useLesDraad";

import {
  copilootVraag,
  INSTELLING_ALLEEN_AFWIJKINGEN,
  v6Api,
  type CopilootBericht,
  type TegelConcept,
  type V6Controle,
} from "../api";
import { useV6 } from "../context";
import { kanaalUitVraag } from "./kanaalUitVraag";
import { DossierBlok } from "./DossierBlok";
import { Icoon } from "../Icoon";
import { v6Sleutels } from "../sleutels";
import type { V6Dossier } from "@/types/api";
import "./controles.css";

const MAX_VELD = 140;
/** Sessie 84: een les ("leer dit ...") is vaak een lange zin; dan groeit
 *  het veld verder mee. Boven 2000 tekens kan hij niet weg (teller). */
const MAX_VELD_LES = 280;
const FOUT_ALGEMEEN = "De copiloot kon niet antwoorden. Probeer het zo opnieuw.";

interface Beurt {
  vraag: string;
  antwoord: CopilootBericht | null;
}

export function Copilootvlak({
  zaakId,
  smal,
  verborgen,
}: {
  zaakId: string | null;
  smal: boolean;
  verborgen: boolean;
}) {
  const { sluitRechts, zetConcept, zetBijlagen, registreerCopiloot, openRechts, leesConcept } =
    useV6();
  const queryClient = useQueryClient();

  const controles = useQuery({
    queryKey: v6Sleutels.controles(zaakId ?? ""),
    queryFn: () => v6Api.controles(zaakId as string),
    enabled: Boolean(zaakId),
  });
  const instellingen = useQuery({
    queryKey: ["v6", "instellingen"],
    queryFn: () => v6Api.instellingen(),
    staleTime: 60_000,
  });
  const draad = useQuery({
    queryKey: v6Sleutels.copiloot(zaakId ?? ""),
    queryFn: () => v6Api.copilootDraad(zaakId as string),
    enabled: Boolean(zaakId),
  });

  const [lijstOpen, setLijstOpen] = useState<boolean | null>(null);
  const [invoer, setInvoer] = useState("");
  const [beurt, setBeurt] = useState<Beurt | null>(null);
  const [stream, setStream] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  // Sessie 84: de lessen van deze zaak, ook de open voorstellen van
  // eerder (na herladen of zaakwissel weer in beeld).
  const {
    beurten: lessen,
    bezig: lesBezig,
    stuur: maakLes,
    sluit: sluitLes,
    laadFout: lessenLaadFout,
  } = useLesDraad(zaakId ? { soort: "zaak", id: zaakId } : null);

  const afbreker = useRef<AbortController | null>(null);
  const veld = useRef<HTMLTextAreaElement | null>(null);
  const scrol = useRef<HTMLDivElement | null>(null);

  // Een andere zaak (nieuwe key) of weg: lopende beurt afbreken.
  useEffect(() => () => afbreker.current?.abort(), []);

  const kanChatten = draad.data?.kan_chatten !== false;

  const stuur = useCallback(
    async (ruw: string, uitVeld: boolean) => {
      const tekst = ruw.trim();
      if (!tekst || !zaakId) return;
      if (bezig) {
        // Niet stil laten vallen: de vraag wacht in het veld.
        if (!uitVeld) setInvoer(tekst);
        return;
      }
      if (!kanChatten) {
        setFout(draad.data?.reden_uit || FOUT_ALGEMEEN);
        return;
      }
      const ctrl = new AbortController();
      afbreker.current = ctrl;
      setFout(null);
      setBeurt({ vraag: tekst, antwoord: null });
      setStream("");
      setBezig(true);
      if (uitVeld) setInvoer("");
      try {
        await copilootVraag(zaakId, tekst, {
          signal: ctrl.signal,
          // Wat er nu in het vak staat: "maak korter" gaat over déze tekst.
          huidigConcept: leesConcept(),
          onDelta: (deel) => {
            if (!ctrl.signal.aborted) setStream((oud) => (oud ?? "") + deel);
          },
          onHerkansing: () => {
            if (!ctrl.signal.aborted) setStream("");
          },
          onKlaar: (bericht) => {
            if (ctrl.signal.aborted) return;
            setBeurt({ vraag: tekst, antwoord: bericht });
            setStream(null);
            if (bericht.concept_tekst) {
              const tekstNieuw = bericht.concept_tekst;
              const onderwerpNieuw = bericht.concept_onderwerp;
              // Direct in het vak, en ook in de concept-cache: wisselt het
              // vak van stand of wordt het opnieuw gemaakt, dan staat de
              // aangepaste tekst er nog. Bewust geen invalidatie van het
              // concept: de server geeft dezelfde tekst (hij leest de beurt
              // van de copiloot), en een trage ophaalronde mag de tekst
              // niet terugzetten.
              queryClient.setQueryData<TegelConcept>(v6Sleutels.concept(zaakId), (oud) =>
                oud
                  ? {
                      ...oud,
                      tekst: tekstNieuw,
                      bron: "copiloot",
                      voorstel_stand: "klaar",
                      reden_leeg: null,
                      onderwerp:
                        oud.kanaal === "mail" && onderwerpNieuw ? onderwerpNieuw : oud.onderwerp,
                    }
                  : oud,
              );
              // Alleen JP's eigen vraag kiest een kanaal; de vraag die Beslissen
              // zelf stelt (met de keuzetekst van Nova) niet.
              zetConcept(zaakId, tekstNieuw, onderwerpNieuw, uitVeld ? kanaalUitVraag(tekst) : null);
              // Kaart en kop zeggen nu "concept klaar".
              void queryClient.invalidateQueries({ queryKey: v6Sleutels.lijst });
              void queryClient.invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId) });
            }
            // Sessie 55: label en pakbon van een retour gaan bij de
            // bijlagen van het antwoordvak, alsof JP ze zelf koos.
            if (bericht.bijlagen?.length) zetBijlagen(zaakId, bericht.bijlagen);
            void queryClient.invalidateQueries({ queryKey: v6Sleutels.copiloot(zaakId) });
          },
          onFout: (melding) => {
            if (ctrl.signal.aborted) return;
            setFout(melding || FOUT_ALGEMEEN);
            setStream(null);
          },
        });
      } catch {
        if (!ctrl.signal.aborted) {
          setFout(FOUT_ALGEMEEN);
          setStream(null);
        }
      } finally {
        if (!ctrl.signal.aborted) {
          setBezig(false);
          setStream((oud) => (oud === "" ? null : oud));
        }
      }
    },
    [
      zaakId,
      bezig,
      kanChatten,
      draad.data?.reden_uit,
      zetConcept,
      zetBijlagen,
      queryClient,
      leesConcept,
    ],
  );

  // Sessie 84: "leer dit ..." uit het veld (getypt of ingesproken) gaat
  // niet naar de copiloot-stream maar wordt een lesvoorstel voor deze zaak.
  const stuurLes = useCallback(
    async (ruw: string) => {
      const tekst = ruw.trim();
      if (!tekst || !zaakId || lesBezig || bezig) return;
      if (!kanChatten) {
        setFout(draad.data?.reden_uit || FOUT_ALGEMEEN);
        return;
      }
      // Te lang: de teller onder het veld zegt waarom er niets gebeurt.
      if (lesTeLang(tekst)) return;
      setFout(null);
      setInvoer("");
      await maakLes(tekst);
    },
    [zaakId, lesBezig, bezig, kanChatten, draad.data?.reden_uit, maakLes],
  );

  const verstuurVeld = useCallback(() => {
    if (isLeerDit(invoer)) void stuurLes(invoer);
    else void stuur(invoer, true);
  }, [invoer, stuur, stuurLes]);

  // Het midden (of een ander vlak) stelt de copiloot een vraag.
  const stuurRef = useRef(stuur);
  const smalRef = useRef(smal);
  useEffect(() => {
    stuurRef.current = stuur;
    smalRef.current = smal;
  }, [stuur, smal]);
  useEffect(() => {
    registreerCopiloot((tekst: string) => {
      if (smalRef.current) openRechts("copiloot");
      void stuurRef.current(tekst, false);
    });
    return () => registreerCopiloot(null);
  }, [registreerCopiloot, openRechts]);

  // Veld groeit mee tot 140 px, bij een les tot 280 px (Safari kent
  // field-sizing nog niet overal).
  const lesInvoer = isLeerDit(invoer);
  const lesTeLangNu = lesInvoer && lesTeLang(invoer);
  useLayoutEffect(() => {
    const el = veld.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, lesInvoer ? MAX_VELD_LES : MAX_VELD)}px`;
  }, [invoer, lesInvoer]);

  const berichten = draad.data?.berichten ?? [];
  const inDraad = new Set(berichten.map((b) => b.id));
  const toonBeurt = beurt && !(beurt.antwoord && inDraad.has(beurt.antwoord.id)) ? beurt : null;

  // Nieuwe bubbel of streamtekst: naar onderen.
  useEffect(() => {
    const el = scrol.current;
    if (el) el.scrollTop = el.scrollHeight;
    // Ook als de controles erboven laden: dan krimpt het gesprek.
  }, [berichten.length, toonBeurt, stream, fout, controles.data, lessen]);

  const opToets = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      verstuurVeld();
    }
  };

  // Controles.
  const items = controles.data?.items ?? [];
  // Sessie 161: "geen klant gekoppeld" is bij een bezoeker geen alarm maar
  // een gegeven; rood blijft voor echte afwijkingen.
  const zonderKlant = items.some(isZonderKlant);
  const afwijkingen = items.filter((c) => c.uitkomst !== "ok" && !isZonderKlant(c));
  const alleenTwijfel =
    afwijkingen.length > 0 && afwijkingen.every((c) => c.uitkomst === "twijfel");
  const instelling = instellingen.data?.waarden[INSTELLING_ALLEEN_AFWIJKINGEN];
  const lijstIsOpen = lijstOpen ?? instelling === false;

  const leegGesprek = berichten.length === 0 && !toonBeurt && lessen.length === 0;

  return (
    <section
      className="v6-vlak v6-copivlak"
      aria-label="Copiloot"
      aria-hidden={verborgen || undefined}
    >
      <div className="ck">
        <span className="ic">
          <Icoon naam="copiloot" klein />
        </span>
        Copiloot <small>alleen deze zaak</small>
        <button
          type="button"
          className="sluitvlak"
          aria-label="Sluiten"
          title="Sluiten"
          onClick={sluitRechts}
        >
          <Icoon naam="x" klein />
        </button>
      </div>

      {!zaakId ? (
        <p className="v6-leeg">Geen zaak gekozen.</p>
      ) : (
        <>
          <div className="ccheck">
            {controles.isError ? (
              <p className="v6-fout" role="alert">
                De controles konden niet geladen worden.
              </p>
            ) : null}

            {zonderKlant ? (
              <p className="neutraal" data-testid="zonder-klant">
                Nog geen klantkaart bij dit gesprek.
              </p>
            ) : null}

            {afwijkingen.length > 0 ? (
              <div className="check mis" data-testid="afwijkingen">
                <div className="t">
                  <Icoon naam="x" klein />
                  {alleenTwijfel ? "Twijfel" : "Niet gevonden"}
                </div>
                {afwijkingen.map((c, i) => (
                  <ControleRegel key={`mis-${i}`} controle={c} />
                ))}
              </div>
            ) : null}

            {controles.data ? (
              <div className={`check${lijstIsOpen ? "" : " dicht"}`}>
                <button
                  type="button"
                  className="t"
                  aria-expanded={lijstIsOpen}
                  aria-controls="v6-gecontroleerd"
                  onClick={() => setLijstOpen(!lijstIsOpen)}
                >
                  <span className="wat">{`Wat is gecontroleerd (${items.length})`}</span>
                  <span className="titel">
                    {controles.data.titel}
                    <span className="pijl">
                      <Icoon naam="pijl" klein />
                    </span>
                  </span>
                </button>
                <div className="bi" id="v6-gecontroleerd">
                  {lijstIsOpen
                    ? items.map((c, i) => <ControleRegel key={`c-${i}`} controle={c} />)
                    : null}
                </div>
              </div>
            ) : null}

            {/* Sessie 86: het dossier van de laatste motorbeurt, standaard dicht. */}
            <DossierBlok dossier={dossierVan(controles.data)} />
          </div>

          <div className="cscrol" ref={scrol}>
            {berichten.map((b) => (
              <Bubbel key={b.id} bericht={b} />
            ))}
            {toonBeurt ? (
              <>
                <div className="cbub jp">{toonBeurt.vraag}</div>
                {toonBeurt.antwoord ? (
                  <Bubbel bericht={toonBeurt.antwoord} />
                ) : stream !== null ? (
                  <div className="cbub" aria-live="polite" data-testid="copiloot-stream">
                    {stream || "..."}
                  </div>
                ) : null}
              </>
            ) : null}
            {lessenLaadFout ? (
              <div className="cbub leeg" data-testid="lessen-laadfout">
                {lessenLaadFout}
              </div>
            ) : null}
            {lessen.map((b) => (
              <div key={b.sleutel} className="les-beurt" data-testid="les-beurt">
                {b.vraag ? <div className="cbub jp">{b.vraag}</div> : null}
                {b.les ? (
                  <LesKaart les={b.les} onSluit={() => sluitLes(b)} />
                ) : b.fout ? (
                  <p className="v6-fout" role="alert" data-testid="les-fout-maken">
                    {b.fout}
                  </p>
                ) : (
                  <div className="cbub leeg" role="status" data-testid="les-bezig">
                    De copiloot schrijft de les uit en controleert de vangrails.
                  </div>
                )}
              </div>
            ))}
            {leegGesprek ? (
              <div className="cbub leeg">
                Stel een vraag over deze zaak of zeg wat er anders moet in het antwoord.
              </div>
            ) : null}
            {fout ? (
              <p className="v6-fout" role="alert">
                {fout}
              </p>
            ) : null}
          </div>

          {!kanChatten && draad.data?.reden_uit ? (
            <p className="uitreden">{draad.data.reden_uit}</p>
          ) : null}
          <div className="cin">
            <textarea
              ref={veld}
              className={lesInvoer ? "les-lang" : undefined}
              rows={1}
              value={invoer}
              placeholder="Vraag de copiloot"
              aria-label="Vraag de copiloot"
              aria-describedby={lesInvoer ? "v6-les-teller" : undefined}
              disabled={!kanChatten}
              onChange={(e) => setInvoer(e.target.value)}
              onKeyDown={opToets}
            />
            <button
              type="button"
              aria-label="Versturen"
              title="Versturen"
              disabled={!kanChatten || bezig || lesBezig || lesTeLangNu || !invoer.trim()}
              onClick={verstuurVeld}
            >
              <Icoon naam="stuur" klein />
            </button>
          </div>
          {lesInvoer ? <LesTeller tekst={invoer} id="v6-les-teller" /> : null}
        </>
      )}
    </section>
  );
}

/** Het veld `dossier` van de controles (sessie 86); null zonder kladblok. */
function dossierVan(data: unknown): V6Dossier | null {
  return (data as { dossier?: V6Dossier | null } | undefined)?.dossier ?? null;
}

/**
 * De controle "Geen klant gekoppeld aan deze zaak" (portaal): bij een
 * bezoeker in de chat normaal, dus neutraal en niet rood (sessie 161).
 */
function isZonderKlant(controle: V6Controle): boolean {
  return (
    controle.uitkomst === "niet_gevonden" &&
    controle.bron === "Portaal" &&
    /geen klant gekoppeld/i.test(controle.tekst)
  );
}

function ControleRegel({ controle }: { controle: V6Controle }) {
  const neutraal = isZonderKlant(controle);
  const mis = controle.uitkomst !== "ok" && !neutraal;
  return (
    <div className={`c${mis ? " mis" : ""}`}>
      <Icoon naam={mis ? "x" : neutraal ? "min" : "ok"} klein />
      <div>
        {controle.tekst} <small>{controle.bron}</small>
      </div>
    </div>
  );
}

function Bubbel({ bericht }: { bericht: CopilootBericht }) {
  const vanJp = bericht.rol === "gebruiker";
  return (
    <div className={`cbub${vanJp ? " jp" : ""}`}>
      {bericht.inhoud}
      {!vanJp && bericht.concept_tekst ? (
        <span className="aangepast">
          <Icoon naam="ok" klein />
          Aangepast in het antwoord
        </span>
      ) : null}
    </div>
  );
}
